import { AlertCircle, ArrowRight, BarChart3, FileText, Play, Upload, Zap, Eye, RefreshCw, Edit3, Save, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { tokenStorage } from '../utils/tokenStorage';
import { useQueryClient } from '@tanstack/react-query';
import api from '../api';
import ClearCacheButton from '../components/ProcessMaps/ClearCache';
import ProcessMapLoader from '../components/ProcessMaps/ProcessMapLoader';
import SwimlanesVisualization from '../components/ProcessMaps/SwimlanesVisualization';
import DrawIOClientEditor from '../components/ProcessMaps/DrawIOClientEditor';
import FrictionLegend from '../components/ProcessMaps/FrictionLegend';
import FrictionHeatmapChart from '../components/ProcessMaps/FrictionHeatmapChart';
import useFrictionMetrics from '../hooks/useFrictionMetrics';
import Tooltip from '../components/ProcessMaps/Tooltip';
import ValidationReportPanel from '../components/ProcessMaps/ValidationReportPanel';
import ValidationCommandCenter from '../components/ProcessMaps/ValidationCommandCenter';
import ProcessMapStatistics from '../components/ProcessMaps/ProcessMapStatistics';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { procesMapUpdateManager } from '../App';



interface ProcessMap {
  id: number;
  level: number;
  map_data: any;
  audit_trail?: any;
  generation_metadata?: any;
  data_lineage?: any;
  title: string;
  complexity_score: number;
  created_at: string;
  version_number?: number;
  edited_by?: string;
  updated_at: string;
  exception_coverage_score?: number;
  total_exceptions?: number;
  escalation_hierarchy?: any;
  process_name: string;
  process_id: string;
}

interface AnalysisProgress {
  progress: number;
  completed: number;
  failed: number;
  pending: number;
  total: number;
}

const ProcessMaps: React.FC = () => {

  const { currentProject } = useProject();
  const { user } = useAuth();
  const queryClient = useQueryClient(); // ✅ React Query client for cache invalidation
  const [selectedLevel, setSelectedLevel] = useState(1); // Default to Level 1 (Strategic)
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(75);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editedMapData, setEditedMapData] = useState<any>(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const [enhancedProcesses, setEnhancedProcesses] = useState<any[]>([]);
  const [loadingRoleAssignments, setLoadingRoleAssignments] = useState(false);
  const loadingRef = useRef(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [allProcessMaps, setAllProcessMaps] = useState<ProcessMap[]>([]);

  const [auditEnhancementStatus, setAuditEnhancementStatus] = useState<{
    status: 'not_started' | 'in_progress' | 'completed' | 'failed';
    progress: number;
    enhanced_steps_count: number;
    total_steps: number;
    error?: string;
  } | null>(null);

  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [hasDocuments, setHasDocuments] = useState<boolean>(false);
  const [hasAnalysis, setHasAnalysis] = useState<boolean>(false);
  const [hasSynthesis, setHasSynthesis] = useState<boolean>(false);
  const [documentCount, setDocumentCount] = useState<number>(0);

  const [loaderOperation, setLoaderOperation] = useState<'generating' | 'switching' | 'loading' | 'regenerating' | 'syncing'>('loading');
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [loaderStage, setLoaderStage] = useState('');
  const [showLoader, setShowLoader] = useState(false);

  const [currentMap, setCurrentMap] = useState<ProcessMap | null>(null);

  // Professional diagram state
  const [diagramMode, setDiagramMode] = useState<'interactive' | 'professional'>('interactive');

  // Validation Command Center state
  const [showCommandCenter, setShowCommandCenter] = useState(false);
  const [validationId, setValidationId] = useState<number | null>(null);
  const [validationStatus, setValidationStatus] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState<{
    completed_steps?: number;
    total_steps?: number;
    estimated_progress?: number;
  } | null>(null);
  const validationPollingRef = useRef<NodeJS.Timeout | null>(null);
  const [mapRefreshCounter, setMapRefreshCounter] = useState(0); // ✅ FIX: Force re-render after validation updates

  // Gap Statistics Panel state
  const [showGapStatistics, setShowGapStatistics] = useState(false);

  // Friction Heatmap state
  const [showFrictionHeatmap, setShowFrictionHeatmap] = useState(false);
  const [showHeatmapChart, setShowHeatmapChart] = useState(false);
  const {
    frictionData,
    frictionMap,
    isLoading: frictionLoading,
    isEstimating: frictionEstimating,
    fetchFrictionData,
    estimateFriction,
    clearFrictionData
  } = useFrictionMetrics();

  // ✅ NEW: Check audit enhancement status for Level 3
  const checkAuditEnhancementStatus = useCallback(async () => {
    if (!currentProject?.id || selectedLevel !== 3) return;

    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.get(`/api/analysis/audit-enhancement-status/${currentProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      setAuditEnhancementStatus({
        status: data.enhancement_status,
        progress: data.enhancement_progress || 0,
        enhanced_steps_count: data.enhanced_steps_count || 0,
        total_steps: data.total_steps || 0,
        error: data.enhancement_error
      });

      // Show status if enhancement is in progress or completed
      if (data.enhancement_status === 'in_progress' || data.enhancement_status === 'completed') {
        // Status will be visible via auditEnhancementStatus state
      }

    } catch (error) {
      console.error('Failed to check audit enhancement status:', error);
    }
  }, [currentProject?.id, selectedLevel]);

  // Fetch validation status for Command Center button
  const fetchValidationStatus = useCallback(async () => {
    if (!currentProject?.id || !currentMap?.id) {
      setValidationId(null);
      setValidationStatus(null);
      return;
    }

    try {
      const response = await api.get(`/api/validation/process-map/${currentMap.id}/validation-status`);
      const status = response.data?.status || null;
      setValidationStatus(status);

      // Set progress data
      if (response.data) {
        setValidationProgress({
          completed_steps: response.data.completed_steps,
          total_steps: response.data.total_steps,
          estimated_progress: response.data.estimated_progress
        });
      }

      if (response.data?.validation_id) {
        if (status === 'COMPLETED') {
          setValidationId(response.data.validation_id);
          setValidating(false);
        } else if (status === 'IN_PROGRESS') {
          setValidationId(null);
          setValidating(true);
        } else {
          setValidationId(null);
          setValidating(false);
        }
      } else {
        setValidationId(null);
        setValidating(false);
      }
    } catch (error) {
      setValidationId(null);
      setValidationStatus(null);
      setValidating(false);
      setValidationProgress(null);
    }
  }, [currentProject?.id, currentMap?.id]);

  // Start polling for validation progress
  const startValidationPolling = useCallback(() => {
    // Clear any existing polling
    if (validationPollingRef.current) {
      clearInterval(validationPollingRef.current);
    }

    validationPollingRef.current = setInterval(async () => {
      if (!currentMap?.id) return;

      try {
        const statusRes = await api.get(`/api/validation/process-map/${currentMap.id}/validation-status`);
        setValidationStatus(statusRes.data?.status || null);
        setValidationProgress({
          completed_steps: statusRes.data?.completed_steps,
          total_steps: statusRes.data?.total_steps,
          estimated_progress: statusRes.data?.estimated_progress
        });

        if (statusRes.data?.status === 'COMPLETED') {
          if (validationPollingRef.current) {
            clearInterval(validationPollingRef.current);
            validationPollingRef.current = null;
          }
          setValidationId(statusRes.data.validation_id);
          setValidating(false);

          // ✅ Force re-render of visualization - this will trigger reload via key change
          setMapRefreshCounter(prev => prev + 1);

          toast.success('Validation completed');
          setShowCommandCenter(true);
        } else if (statusRes.data?.status === 'FAILED') {
          if (validationPollingRef.current) {
            clearInterval(validationPollingRef.current);
            validationPollingRef.current = null;
          }
          setValidating(false);
          toast.error('Validation failed');
        }
      } catch (err) {
        // Continue polling on transient errors
      }
    }, 3000);

    // Timeout after 10 minutes
    setTimeout(() => {
      if (validationPollingRef.current) {
        clearInterval(validationPollingRef.current);
        validationPollingRef.current = null;
      }
      setValidating(false);
    }, 600000);
  }, [currentMap?.id]);

  // Start validation from Command Center button
  const startValidationFromButton = async () => {
    if (!currentProject?.id || !currentMap?.id) return;

    setValidating(true);
    try {
      await api.post(`/api/validation/process-map/${currentMap.id}/validate`, {
        project_id: currentProject.id
      });
      toast.success('Validation started');

      // ✅ Force re-render to show validation started state
      setMapRefreshCounter(prev => prev + 1);

      startValidationPolling();

    } catch (error: any) {
      // Handle 409 Conflict - validation already in progress
      if (error.response?.status === 409) {
        toast.success('Validation already in progress');
        startValidationPolling();
      } else {
        console.error('Failed to start validation:', error);
        toast.error('Failed to start validation');
        setValidating(false);
      }
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (validationPollingRef.current) {
        clearInterval(validationPollingRef.current);
      }
    };
  }, []);

  // Fetch validation status when map changes
  useEffect(() => {
    fetchValidationStatus();
  }, [fetchValidationStatus]);

  const checkProjectPrerequisites = useCallback(async () => {
    if (!currentProject?.id) return;

    try {
      const token = localStorage.getItem('jarvis_access_token');

      // Get documents
      const documentsResponse = await api.get(`/api/documents/project/${currentProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const documents = documentsResponse.data.documents || [];
      setHasDocuments(documents.length > 0);
      setDocumentCount(documents.length);

      // Get analysis results
      const analysisResponse = await api.get(`/api/analysis/results/${currentProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const analysisResults = analysisResponse.data;
      const analyzedDocuments = analysisResults?.document_analyses || [];

      // Check if ALL current documents are analyzed using TWO methods:
      // Method 1: Check if document analysis records exist for all documents
      const analyzedDocumentIds = new Set(analyzedDocuments.map((a: any) => a.Document?.id).filter(Boolean));
      const hasAnalysisRecords = documents.length > 0 && documents.every((doc: any) => analyzedDocumentIds.has(doc.id));

      // Method 2: Check if all documents have analysis_status = 'completed'
      const hasCompletedStatus = documents.length > 0 && documents.every((doc: any) => doc.analysis_status === 'completed');

      // Use EITHER method - if documents have completed status OR have analysis records
      const allDocumentsAnalyzed = documents.length > 0 && (hasAnalysisRecords || hasCompletedStatus);

      setHasAnalysis(allDocumentsAnalyzed);

      // Check process synthesis - but only if we have analysis for all documents
      let hasSynthesisData = false;
      if (allDocumentsAnalyzed) {
        try {
          const synthesisResponse = await api.get(`/api/analysis/process-synthesis/${currentProject.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const synthesis = synthesisResponse.data;
          hasSynthesisData = !!(synthesis && (synthesis.process_steps || synthesis.synthesis_text));
        } catch (synthError) {
          // If synthesis endpoint fails, assume no synthesis exists
          hasSynthesisData = false;
        }
      }
      setHasSynthesis(hasSynthesisData);

    } catch (error) {
      console.error('Error checking project prerequisites:', error);
      // Reset states on error
      setHasDocuments(false);
      setHasAnalysis(false);
      setHasSynthesis(false);
      setDocumentCount(0);
    }
  }, [currentProject?.id]);

  const loadProcessMaps = useCallback(async () => {
    if (!currentProject) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('jarvis_access_token');

      const timestamp = Date.now();

      // Load ALL process maps for this project
      const allMapsResponse = await api.get(
        `/api/process-maps/project/${currentProject.id}?_t=${timestamp}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
        }
      );

      const allMaps = allMapsResponse.data.process_maps || [];

      // Validate each map has proper structure
      const validatedMaps = allMaps.map((map: { map_data: { elements: { processes: any; }; }; id: any; }) => {
        if (!map.map_data) {
          console.warn(`Map ${map.id} has no map_data`);
          return map;
        }

        if (!map.map_data.elements) {
          console.warn(`Map ${map.id} has map_data but no elements structure - data may be corrupted`);
        }

        return map;
      });

      setAllProcessMaps(validatedMaps);

      // Set current map based on selected level
      const currentLevelMap = validatedMaps.find((map: { level: number; }) => map.level === selectedLevel);

      if (currentLevelMap) {
        setCurrentMap(currentLevelMap);
      } else {
        setCurrentMap(null);
      }

    } catch (error: any) {
      if (error.response?.status !== 404) {
        toast.error('Failed to load process maps');
      }
      // Reset states on error
      setAllProcessMaps([]);
      setCurrentMap(null);
    } finally {
      setLoading(false);
    }
  }, [currentProject, selectedLevel]);

  useEffect(() => {
    if (currentProject?.id) {
      checkProjectPrerequisites();
      loadProcessMaps();
      loadAnalysisResults();
      checkAuditEnhancementStatus();
    }
  }, [currentProject?.id, checkAuditEnhancementStatus]);

  // ✅ Reload process maps when mapRefreshCounter changes (triggered by validation updates)
  useEffect(() => {
    if (currentProject?.id && mapRefreshCounter > 0) {
      loadProcessMaps();
    }
  }, [mapRefreshCounter, currentProject?.id, loadProcessMaps]);

  // ✅ NEW: Listen for auto-sync events from Stakeholder Tab
  useEffect(() => {
    const handleProcessMapsUpdated = (event: CustomEvent) => {
      const { projectId } = event.detail;

      // Only reload if it's for the current project
      if (projectId === currentProject?.id) {
        console.log('🔄 Auto-sync completed, reloading process maps...');

        // Reload process maps to show updated data
        loadProcessMaps();

        // Show a subtle notification
        toast.success('Process maps updated!', {
          duration: 3000,
          icon: '🔄'
        });
      }
    };

    window.addEventListener('process-maps-updated' as any, handleProcessMapsUpdated);

    return () => {
      window.removeEventListener('process-maps-updated' as any, handleProcessMapsUpdated);
    };
  }, [currentProject?.id, loadProcessMaps]);

  // ✅ REMOVED: No longer need state restoration since we're not doing page reload
  // React Query handles cache invalidation and refetch automatically

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentProject?.id) {
        checkProjectPrerequisites();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentProject?.id, checkProjectPrerequisites])

  const processLevels = [
    { value: 1, name: 'Level 1: Strategic', description: 'High-level business objectives and outcomes' },
    { value: 2, name: 'Level 2: Tactical', description: 'Core business processes and cross-functional workflows' },
    { value: 3, name: 'Level 3: Operational', description: 'Detailed process steps and procedures' },
    { value: 4, name: 'Level 4: Execution', description: 'Task-level activities and system interactions' }
  ];

  const loadAnalysisProgress = useCallback(async () => {
    if (!currentProject) return;
    try {
      const response = await api.get(`/api/analysis/progress/${currentProject.id}`);
      setAnalysisProgress(response.data);
    } catch (error) {
      console.error('Failed to load analysis progress');
    }
  }, [currentProject]);

  const loadAnalysisResults = useCallback(async () => {
    if (!currentProject) return;
    try {
      // FIXED: Remove cache headers, use URL timestamp instead
      const timestamp = Date.now();
      const response = await api.get(`/api/analysis/results/${currentProject.id}?_t=${timestamp}`);

      setAnalysisResults(response.data);

    } catch (error) {
      console.error('Failed to load analysis results:', error);
    }
  }, [currentProject]);


  useEffect(() => {
    if (currentProject) {
      loadAnalysisProgress();
      loadAnalysisResults();
    }
  }, [currentProject, loadAnalysisProgress, loadAnalysisResults]);

  useEffect(() => {
    let resultsInterval: NodeJS.Timeout | null = null;
    if (isAnalyzing && currentProject) {
      resultsInterval = setInterval(() => {
        loadAnalysisResults();
      }, 3000);
    }

    return () => {
      if (resultsInterval) {
        clearInterval(resultsInterval);
        resultsInterval = null;
      }
    };
  }, [isAnalyzing, loadAnalysisResults, currentProject]);

  useEffect(() => {
    if (isAnalyzing && analysisProgress) {
      const { completed, failed, total } = analysisProgress;
      if ((completed + failed === total) && total > 0) {
        setIsAnalyzing(false);
        loadAnalysisResults();
      }
    }
  }, [isAnalyzing, analysisProgress, loadAnalysisResults]);

  useEffect(() => {
    // Only run post-analysis polling if we're NOT analyzing AND we don't have complete synthesis data
    if (isAnalyzing || !currentProject) {
      return;
    }

    // Check if we have complete analysis results
    const hasSynthesis = analysisResults?.process_synthesis;
    const hasAnalysisComplete = analysisResults && hasSynthesis;

    // If we have complete results, don't poll
    if (hasAnalysisComplete) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 20; // Reduced from 40 to prevent excessive polling
    let intervalId: NodeJS.Timeout | null = null;

    intervalId = setInterval(() => {
      if (attempts >= maxAttempts) {
        console.warn("Post-analysis polling timed out after 20 attempts.");
        if (intervalId) clearInterval(intervalId);
        return;
      }

      if (currentProject) {
        loadAnalysisResults();
        attempts++;
      } else {
        if (intervalId) clearInterval(intervalId);
      }
    }, 5000); // Increased from 3000 to 5000 to reduce frequency

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [isAnalyzing, analysisResults?.process_synthesis, analysisResults?.sop, loadAnalysisResults, currentProject]);


  const generateMapForLevel = async (level: number) => {
    if (!currentProject || generating) return;

    setGenerating(true);
    setSelectedLevel(level);
    setShowLoader(true);
    setLoaderOperation(allProcessMaps.find(map => map.level === level) ? 'regenerating' : 'generating');
    setLoaderProgress(0);
    setLoaderStage('Starting process map generation...');

    try {
      let token = tokenStorage.getAccessToken();

      const existingMap = allProcessMaps.find(map => map.level === level);
      const requestConfig: any = { timeout: 30000 };
      if (token) {
        requestConfig.headers = { Authorization: `Bearer ${token}` };
      }

      // ✅ FIX: Only send force_regenerate=true when REGENERATING (existing map exists)
      // This allows preserve mode to work on first generation
      const isRegenerating = existingMap ? true : false;

      const startResponse = await api.post(
        `/api/analysis/generate-process-map-async/${currentProject.id}`,
        {
          level: level,
          force_regenerate: isRegenerating,  // ✅ Only true when regenerating
          delete_existing: isRegenerating    // ✅ Same condition
        },
        requestConfig
      );

      if (startResponse.data.status !== 'in_progress') {
        throw new Error('Failed to start generation process');
      }

      const generationId = startResponse.data.generation_id;

      setLoaderProgress(10);
      setLoaderStage('Generation started successfully...');

      // Polling logic (unchanged from your working version)
      let attempts = 0;
      const maxAttempts = level === 4 ? 200 : 120;
      let isCompleted = false;

      const pollStatus = async (): Promise<boolean> => {
        try {
          const statusResponse = await api.get(`/api/analysis/generate-process-map-status/${generationId}`, {
            timeout: 300000
          });

          const status = statusResponse.data;

          if (status.progress >= 0) {
            setLoaderProgress(status.progress);
          }

          if (status.message) {
            setLoaderStage(status.message);
          }

          if (status.status === 'completed') {
            setLoaderProgress(95);
            setLoaderStage('Process map completed! Loading results...');
            return true;
          } else if (status.status === 'failed') {
            throw new Error(status.error || 'Generation failed');
          }

          return false;
        } catch (statusError: any) {
          console.warn(`Status check ${attempts + 1} failed:`, statusError.message);

          if (attempts > 60) {
            throw new Error('Lost connection to generation process');
          }

          return false;
        }
      };

      while (attempts < maxAttempts && !isCompleted) {
        await new Promise(resolve => setTimeout(resolve, 10000));
        attempts++;

        try {
          isCompleted = await pollStatus();
        } catch (pollError) {
          console.error('Polling failed:', pollError);
          throw pollError;
        }
      }

      if (!isCompleted) {
        throw new Error('Generation timed out. The process may still be running in the background.');
      }

      // Success handling (unchanged)
      setLoaderStage('Loading completed process map...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      await forceRefreshProcessData();

      setLoaderProgress(100);
      setLoaderStage('Complete!');

      setTimeout(() => {
        setShowLoader(false);

        const operationText = loaderOperation === 'regenerating' ? 'regenerated' : 'generated';
        toast.success(`Level ${level} process map ${operationText} successfully!`, {
          duration: 4000,
          icon: '🎯',
        });

        // ✅ NEW: Start checking audit enhancement status for Level 3
        if (level === 3) {
          // Show enhancement status message
          setTimeout(() => {
            toast('🔄 Background: Generating detailed audit info for process steps...', {
              duration: 6000,
              icon: '⚙️',
            });
          }, 2000);

          // Start polling for audit enhancement status
          const pollInterval = setInterval(async () => {
            console.log('🔄 Polling for audit enhancement status...');
            try {
              const token = localStorage.getItem('jarvis_access_token');
              const response = await api.get(`/api/analysis/audit-enhancement-status/${currentProject.id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });

              const data = response.data;
              const newStatus = {
                status: data.enhancement_status,
                progress: data.enhancement_progress || 0,
                enhanced_steps_count: data.enhanced_steps_count || 0,
                total_steps: data.total_steps || 0,
                error: data.enhancement_error
              };

              console.log(`🔍 Current audit status: ${newStatus.status} (${newStatus.progress}%)`);
              setAuditEnhancementStatus(newStatus);

              // Stop polling if completed, failed, or not applicable (preserve mode)
              if (newStatus.status === 'completed') {
                console.log('✅ Audit enhancement completed! Stopping polling.');
                clearInterval(pollInterval);

                // Reload process maps to show updated audit data
                console.log('🔄 Reloading process maps with audit data...');
                await loadProcessMaps();

                // Hide the loading spinner
                setShowLoader(false);
                setGenerating(false);

                toast.success('✅ Audit info generation completed!', {
                  duration: 4000,
                  icon: '📋',
                });
              } else if (newStatus.status === 'failed') {
                console.log('❌ Audit enhancement failed! Stopping polling.');
                clearInterval(pollInterval);
                toast.error('❌ Audit info generation failed', {
                  duration: 4000,
                });
              } else if (newStatus.status === 'not_applicable') {
                console.log('🔒 Preserve mode detected - audit enhancement not applicable. Stopping polling.');
                clearInterval(pollInterval);

                // Reload process maps to show the preserved map
                console.log('🔄 Reloading process maps to display preserved document...');
                await loadProcessMaps();

                // Hide the loading spinner
                setShowLoader(false);
                setGenerating(false);

                toast.success('🔒 Process map preserved in original format', {
                  duration: 4000,
                  icon: '✅',
                });
              }
            } catch (error) {
              console.error('❌ Error polling audit status:', error);
            }
          }, 3000);

          // Stop polling after 5 minutes to prevent infinite polling
          setTimeout(() => {
            clearInterval(pollInterval);
          }, 300000);
        }

      }, 1000);

    } catch (error: any) {
      console.error(`Level ${level} process map generation failed:`, error);

      setShowLoader(false);

      const errorMessage = error.response?.data?.message || error.message || 'Generation failed';

      // Simple error handling without auth redirects
      if (errorMessage.includes('timed out')) {
        toast.error(
          `Generation is taking longer than expected. Please check back in a few minutes or refresh the page.`,
          { duration: 8000 }
        );
      } else if (errorMessage.includes('already in progress')) {
        toast.error(
          'Process map generation is already running. Please wait for it to complete.',
          { duration: 5000 }
        );
      } else if (error.response?.status === 401) {
        toast.error('Authentication issue. Please refresh the page and try again.', { duration: 5000 });
      } else {
        toast.error(`Failed to generate Level ${level} process map: ${errorMessage}`, {
          duration: 6000
        });
      }

    } finally {
      setGenerating(false);
    }
  };

  const verifyAuthentication = async (): Promise<string | null> => {
    // Try multiple token storage locations
    const tokenSources = [
      () => localStorage.getItem('jarvis_access_token'),
      () => sessionStorage.getItem('jarvis_access_token')
    ];

    for (const getToken of tokenSources) {
      const token = getToken();
      if (token) {
        return token;
      }
    }

    return null;
  };

  // You can also add this to your component to check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = await verifyAuthentication();
      if (!token) {
        console.warn('No valid authentication found');
        // Handle authentication failure
      }
    };

    if (currentProject) {
      checkAuth();
    }
  }, [currentProject]);


  const generateMap = () => generateMapForLevel(selectedLevel);

  const handleLevelChange = async (newLevel: number) => {
    if (hasUnsavedChanges) {
      const confirmSwitch = window.confirm('You have unsaved changes. Do you want to discard them and switch levels?');
      if (!confirmSwitch) return;
    }

    // Only show loader for level switching if there's an existing map to load
    const existingMap = allProcessMaps.find(map => map.level === newLevel);

    if (existingMap) {
      setShowLoader(true);
      setLoaderOperation('switching');
      setLoaderProgress(30);
      setLoaderStage(`Loading ${processLevels.find(l => l.value === newLevel)?.name}...`);
    }

    // Update states
    setSelectedLevel(newLevel);
    setIsEditMode(false);
    setHasUnsavedChanges(false);
    setEditedMapData(null);

    // Clear cached data
    setEnhancedProcesses([]);

    if (existingMap) {
      // Simulate loading for better UX
      setTimeout(() => setLoaderProgress(70), 200);
      setTimeout(() => setLoaderProgress(100), 400);

      setTimeout(() => {
        setCurrentMap(existingMap);
        setShowLoader(false);
        toast.success(`Loaded ${processLevels.find(l => l.value === newLevel)?.name} process map`);

        // ✅ NEW: Check audit enhancement status when switching to Level 3
        if (newLevel === 3) {
          checkAuditEnhancementStatus();
        }
      }, 600);
    } else {
      setCurrentMap(null);

      if (hasAllPrerequisites) {
        const levelInfo = processLevels.find(l => l.value === newLevel);
        toast(`No process map found for ${levelInfo?.name}. Click "Generate" to create one.`, {
          icon: 'ℹ️',
          duration: 4000,
        });
      }
    }
  };


  const handleZoomChange = (newZoom: number) => {
    setZoomLevel(Math.max(25, Math.min(200, newZoom)));
  };

  const handleProcessReorder = (reorderedProcesses: any[]) => {
    if (!currentMap || !currentMap.map_data) return;

    const updatedMapData = {
      ...currentMap.map_data,
      elements: {
        ...currentMap.map_data.elements,
        processes: reorderedProcesses
      }
    };

    setEditedMapData(updatedMapData);
    setHasUnsavedChanges(true);
  };

  const handleStepDelete = async (stepId: string) => {
    if (!currentProject?.id || !currentMap?.id) {
      toast.error('No project or process map selected');
      return;
    }

    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.delete(`/api/process-maps/${currentMap.id}/steps/${stepId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { project_id: currentProject.id }
      });

      if (response.data.success) {
        toast.success(`Step deleted successfully`);
        setIsEditMode(false);
        setHasUnsavedChanges(false);
        setEditedMapData(null);
        await loadProcessMaps();
        setMapRefreshCounter(prev => prev + 1);
      }
    } catch (error: any) {
      console.error('Failed to delete step:', error);
      toast.error(error.response?.data?.message || 'Failed to delete step');
    }
  };

  const handleSaveChanges = async () => {
    if (!currentMap || !editedMapData || !currentProject) {
      toast.error('No changes to save');
      return;
    }

    setSavingChanges(true);
    try {
      // ✅ COMPREHENSIVE FIX: Clean all L3_ prefixes and filter out track steps before saving
      const cleanMapData = {
        ...editedMapData,
        elements: {
          ...editedMapData.elements,
          processes: editedMapData.elements.processes
            // ⚠️ CRITICAL: Filter out track steps (frontend-only visualization artifacts)
            .filter((step: any) => {
              // Remove steps with track IDs (e.g., "activity_1_TrackA", "activity_1_TrackB")
              if (step.id && typeof step.id === 'string') {
                if (step.id.endsWith('_TrackA') || step.id.endsWith('_TrackB')) {
                  return false;
                }
              }
              // Remove steps explicitly marked as track steps
              if (step.isTrackStep === true) {
                return false;
              }
              return true;
            })
            .map((step: any) => {
              const cleanedStep = { ...step };

              // Helper function to strip L3_ prefix and track suffixes from any step ID
              const cleanStepId = (id: any): any => {
                if (!id) return id;
                if (typeof id === 'string') {
                  // Strip L3_ prefix and _TrackA/_TrackB suffixes
                  return id.replace(/^L3_/, '').replace(/_(TrackA|TrackB)$/, '');
                }
                return id;
              };

              // Clean step ID
              cleanedStep.id = cleanStepId(cleanedStep.id);

              // Clean connections array
              if (cleanedStep.connections && Array.isArray(cleanedStep.connections)) {
                cleanedStep.connections = cleanedStep.connections
                  .map(cleanStepId)
                  // Filter out connections to track steps that no longer exist
                  .filter((conn: string) => conn && !conn.endsWith('_TrackA') && !conn.endsWith('_TrackB'));
              }

              // Clean decisionPaths (camelCase - frontend format)
              if (cleanedStep.decisionPaths) {
                if (cleanedStep.decisionPaths.yes) {
                  cleanedStep.decisionPaths.yes = cleanStepId(cleanedStep.decisionPaths.yes);
                }
                if (cleanedStep.decisionPaths.no) {
                  cleanedStep.decisionPaths.no = cleanStepId(cleanedStep.decisionPaths.no);
                }
                if (cleanedStep.decisionPaths.completion) {
                  cleanedStep.decisionPaths.completion = cleanStepId(cleanedStep.decisionPaths.completion);
                }
                if (cleanedStep.decisionPaths.parallel_tracks) {
                  const tracks = cleanedStep.decisionPaths.parallel_tracks;

                  // Clean track A
                  if (tracks.track_A) {
                    if (tracks.track_A.next_steps && Array.isArray(tracks.track_A.next_steps)) {
                      tracks.track_A.next_steps = tracks.track_A.next_steps.map(cleanStepId);
                    }
                    if (tracks.track_A.convergence) {
                      tracks.track_A.convergence = cleanStepId(tracks.track_A.convergence);
                    }
                  }

                  // Clean track B
                  if (tracks.track_B) {
                    if (tracks.track_B.next_steps && Array.isArray(tracks.track_B.next_steps)) {
                      tracks.track_B.next_steps = tracks.track_B.next_steps.map(cleanStepId);
                    }
                    if (tracks.track_B.convergence) {
                      tracks.track_B.convergence = cleanStepId(tracks.track_B.convergence);
                    }
                  }

                  // Clean global convergence
                  if (tracks.convergence) {
                    tracks.convergence = cleanStepId(tracks.convergence);
                  }
                }
              }

              // Clean decision_paths (snake_case - database format)
              if (cleanedStep.decision_paths) {
                if (cleanedStep.decision_paths.yes) {
                  cleanedStep.decision_paths.yes = cleanStepId(cleanedStep.decision_paths.yes);
                }
                if (cleanedStep.decision_paths.no) {
                  cleanedStep.decision_paths.no = cleanStepId(cleanedStep.decision_paths.no);
                }
                if (cleanedStep.decision_paths.completion) {
                  cleanedStep.decision_paths.completion = cleanStepId(cleanedStep.decision_paths.completion);
                }
                if (cleanedStep.decision_paths.parallel_tracks) {
                  const tracks = cleanedStep.decision_paths.parallel_tracks;

                  // Clean track A
                  if (tracks.track_A) {
                    if (tracks.track_A.next_steps && Array.isArray(tracks.track_A.next_steps)) {
                      tracks.track_A.next_steps = tracks.track_A.next_steps.map(cleanStepId);
                    }
                    if (tracks.track_A.convergence) {
                      tracks.track_A.convergence = cleanStepId(tracks.track_A.convergence);
                    }
                  }

                  // Clean track B
                  if (tracks.track_B) {
                    if (tracks.track_B.next_steps && Array.isArray(tracks.track_B.next_steps)) {
                      tracks.track_B.next_steps = tracks.track_B.next_steps.map(cleanStepId);
                    }
                    if (tracks.track_B.convergence) {
                      tracks.track_B.convergence = cleanStepId(tracks.track_B.convergence);
                    }
                  }

                  // Clean global convergence
                  if (tracks.convergence) {
                    tracks.convergence = cleanStepId(tracks.convergence);
                  }
                }
              }

              // Remove frontend-only fields that should never be saved to database
              delete cleanedStep.isTrackStep;
              delete cleanedStep.trackType;
              delete cleanedStep.originalStepId;

              return cleanedStep;
            })
        }
      };

      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.put(`/api/process-maps/${currentMap.id}/reorder-steps`, {
        map_data: cleanMapData,
        project_id: currentProject.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsEditMode(false);
      setEditedMapData(null);
      setHasUnsavedChanges(false);

      // ✅ FIX: Use React Query to invalidate and refetch instead of page reload
      await queryClient.invalidateQueries({
        queryKey: ['processMaps', currentProject.id]
      });

      await loadProcessMaps();

      toast.success('Changes saved successfully!');

      // ✅ REVALIDATION: Prompt user to validate reordered steps
      setTimeout(() => {
        toast((t) => (
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-md">
            <div className="flex items-start space-x-3">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  Steps have been reordered
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Would you like to validate the new sequence?
                </p>

                {/* Buttons */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      toast.dismiss(t.id);
                      startValidationFromButton();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    Start Validation
                  </button>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Later
                  </button>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={() => toast.dismiss(t.id)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ), {
          duration: 10000,
          position: 'top-center',
          style: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0,
          }
        });
      }, 1000);
    } catch (error: any) {
      console.error('Failed to save changes:', error);
      toast.error(error.response?.data?.message || 'Failed to save changes');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmCancel) return;
    }
    setIsEditMode(false);
    setEditedMapData(null);
    setHasUnsavedChanges(false);
  };

  const handleToggleEditMode = () => {
    if (isEditMode) {
      handleCancelEdit();
    } else {
      setIsEditMode(true);
      setEditedMapData(currentMap?.map_data || null);
    }
  };

  const getProcessData = () => {

    // PRIORITY 1: Use current process map data if available (FRESH DATA)
    if (currentMap && currentMap.map_data) {
      const mapData = isEditMode && editedMapData ? editedMapData : currentMap.map_data;

      if (!mapData || !mapData.elements) {
        console.warn('⚠️ Map data exists but has no elements structure');
        // Try to extract from root level
        if (mapData.processes) {
          mapData.elements = { processes: mapData.processes };
        } else {
          return { processes: [], elements: {}, swimlanes: [] };
        }
      }

      const elements = mapData.elements || {};
      let processes = [];

      if (elements.processes && Array.isArray(elements.processes)) {
        processes = elements.processes.map((process: any, index: number) => {
          return {
            ...process,
            id: process.id || `process_${index}`,
            name: process.name || process.description || `Process ${index + 1}`,
            description: process.detailed_description || process.description || process.name,
            assignee: process.assignee || process.actor || 'Unassigned',
            swimlane: process.assignee || process.actor || 'Unassigned',
            actor: process.actor || process.assignee,
            category: process.action_type,
            type: process.action_type,
            level: process.level || selectedLevel,
            is_decision: process.is_decision || process.action_type === 'decision' || process.bpmn_type === 'gateway',
            step_index: index,
            connections: index < elements.processes.length - 1 ? [`${process.id || `process_${index + 1}`}`] : [],
            sync_source: 'process_map_fresh',
            map_id: currentMap.id,
            updated_at: currentMap.updated_at
          };
        });
      }

      const swimlanes = elements.swimlanes || [
        { id: 'lane_1', name: 'Business Owner', activities: [], description: 'Primary business owner responsibilities' },
        { id: 'lane_2', name: 'Process Owner', activities: [], description: 'Process management and oversight' },
        { id: 'lane_3', name: 'Team Members', activities: [], description: 'Execution and delivery' },
        { id: 'lane_4', name: 'External Partners', activities: [], description: 'Third-party interactions' },
        { id: 'lane_5', name: 'Systems', activities: [], description: 'Automated processes and tools' }
      ];

      return { processes, elements, swimlanes };
    }

    // No fallback - return empty data if no process map exists
    console.warn('❌ No process map data available');
    return { processes: [], elements: {}, swimlanes: [] };
  };


  const forceRefreshProcessData = useCallback(async () => {
    setEnhancedProcesses([]);
    setCurrentMap(null);

    try {
      const timestamp = Date.now();

      const [mapsResult] = await Promise.all([
        api.get(`/api/process-maps/project/${currentProject!.id}?_t=${timestamp}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jarvis_access_token')}`,
          },
        }),
        loadAnalysisResults(),
        checkProjectPrerequisites()
      ]);

      const allMaps = mapsResult.data.process_maps || [];
      setAllProcessMaps(allMaps);

      const currentLevelMap = allMaps.find((map: { level: number; }) => map.level === selectedLevel);
      if (currentLevelMap) {
        setCurrentMap(currentLevelMap);
      }

    } catch (error) {
      throw error;
    }
  }, [currentProject, selectedLevel, loadAnalysisResults, checkProjectPrerequisites]);

  // Handle pending updates after component mount
  useEffect(() => {
    if (currentProject?.id) {
      const pendingUpdates = procesMapUpdateManager.getPendingUpdates();
      if (pendingUpdates.length > 0) {
        setTimeout(() => {
          forceRefreshProcessData().then(() => {
            procesMapUpdateManager.clearAll();
          });
        }, 1000);
      }
    }
  }, [currentProject?.id, forceRefreshProcessData]);


  // Listen for process map updates
  useEffect(() => {
    const handleDirectProcessMapUpdate = (event: CustomEvent) => {
      const { projectId: eventProjectId, processMapId: eventProcessMapId } = event.detail;

      if (currentProject?.id === eventProjectId) {
        forceRefreshProcessData().then(() => {
          if (eventProcessMapId) {
            procesMapUpdateManager.markAsProcessed(eventProcessMapId);
          }
        });
      }
    };

    const handleGlobalProcessMapUpdate = (event: CustomEvent) => {
      const { processMapId } = event.detail;

      if (currentProject?.id && processMapId) {
        forceRefreshProcessData().then(() => {
          procesMapUpdateManager.markAsProcessed(processMapId);
        });
      }
    };

    window.addEventListener('processMapUpdated', handleDirectProcessMapUpdate as EventListener);
    window.addEventListener('globalProcessMapUpdateReceived', handleGlobalProcessMapUpdate as EventListener);

    return () => {
      window.removeEventListener('processMapUpdated', handleDirectProcessMapUpdate as EventListener);
      window.removeEventListener('globalProcessMapUpdateReceived', handleGlobalProcessMapUpdate as EventListener);
    };
  }, [currentProject?.id, forceRefreshProcessData]);


  const { processes: rawProcesses, swimlanes } = useMemo(() => getProcessData(), [currentMap, currentMap?.updated_at, selectedLevel, isEditMode, editedMapData, hasUnsavedChanges]);


  useEffect(() => {
    if (rawProcesses.length > 0 && currentProject) {
      const processesWithAssignees = rawProcesses.map((process: any) => ({
        ...process,
        assignee: process.assignee || 'Unassigned',
        sync_source: 'direct_preserve'
      }));

      setEnhancedProcesses(processesWithAssignees);
      setLoadingRoleAssignments(false);
      loadingRef.current = false;
    } else {
      setEnhancedProcesses([]);
      setLoadingRoleAssignments(false);
    }
  }, [rawProcesses.length, currentProject?.id, selectedLevel, currentMap?.id, currentMap?.updated_at]);

  const processes = isEditMode && editedMapData?.elements?.processes
    ? editedMapData.elements.processes
    : enhancedProcesses;


  useEffect(() => {
    // Auto-retry data loading if current map is missing but should exist
    if (currentProject && !currentMap && !loading && !generating) {
      const mapShouldExist = allProcessMaps.some(map => map.level === selectedLevel);
      if (mapShouldExist) {
        setTimeout(() => {
          loadProcessMaps();
        }, 1000);
      }
    }
  }, [currentProject, currentMap, loading, generating, allProcessMaps, selectedLevel, loadProcessMaps]);


  interface ProcessLevelControlsProps {
    selectedLevel: number;
    onLevelChange: (level: number) => void;
    onGenerateLevel: (level: number) => void;
    generating: boolean;
    hasAllPrerequisites: boolean;
    allProcessMaps?: ProcessMap[];
  }

  const ProcessLevelControls: React.FC<ProcessLevelControlsProps> = ({
    selectedLevel,
    onLevelChange,
    onGenerateLevel,
    generating,
    hasAllPrerequisites,
    allProcessMaps = []
  }) => {


    const levels = [
      {
        value: 1,
        name: 'Strategic',
        description: 'High-level business objectives and outcomes',
        icon: '🎯',
        color: 'bg-blue-500'
      },
      {
        value: 2,
        name: 'Tactical',
        description: 'Core business processes and cross-functional workflows',
        icon: '⚡',
        color: 'bg-green-500'
      },
      {
        value: 3,
        name: 'Operational',
        description: 'Detailed process steps and procedures',
        icon: '🔧',
        color: 'bg-orange-500'
      },
      {
        value: 4,
        name: 'Execution',
        description: 'Task-level activities and system interactions',
        icon: '⚙️',
        color: 'bg-red-500'
      }
    ];

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
          <BarChart3 className="w-5 h-5 text-electric-blue" />
          <span>Process Map Levels</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {levels.map((level) => {
            const isSelected = selectedLevel === level.value;
            const existingMap = allProcessMaps.find(map => map.level === level.value);
            const hasMap = !!existingMap;

            return (
              <div
                key={level.value}
                className={`relative border-2 rounded-lg p-3 transition-all cursor-pointer ${isSelected
                  ? 'border-electric-blue bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
                onClick={() => onLevelChange(level.value)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{level.icon}</span>
                    <span className="font-medium text-gray-900">Level {level.value}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {hasMap && (
                      <div className="w-2 h-2 bg-green-500 rounded-full" title="Generated"></div>
                    )}
                    {isSelected && (
                      <div className="w-2 h-2 bg-electric-blue rounded-full" title="Selected"></div>
                    )}
                  </div>
                </div>

                <h4 className="font-medium text-sm text-gray-900 mb-1">{level.name}</h4>
                <p className="text-xs text-gray-600 mb-4">{level.description}</p>

                {(() => {
                  const canGenerateLevel = (levelValue: number) => {
                    if (!hasAllPrerequisites) return false;

                    if (levelValue === 3) {
                      return true;
                    } else if (levelValue === 4) {
                      return allProcessMaps.some(map => map.level === 3);
                    } else if (levelValue === 2) {
                      return allProcessMaps.some(map => map.level === 3);
                    } else if (levelValue === 1) {
                      // L1 can now be generated without L3 (direct from synthesis)
                      return true;
                    }
                    return false;
                  };

                  const getDisabledReason = (levelValue: number): string => {
                    if (!hasAllPrerequisites) return 'Prerequisites not met';
                    if (levelValue === 4 && !allProcessMaps.some(map => map.level === 3)) {
                      return 'Generate Level 3 first';
                    } else if (levelValue === 2 && !allProcessMaps.some(map => map.level === 3)) {
                      return 'Generate Level 3 first';
                    }
                    // L1 no longer requires L3 - can generate directly from synthesis
                    return 'Prerequisites not met';
                  };

                  const canGenerateThisLevel = canGenerateLevel(level.value);

                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (canGenerateThisLevel) {
                          if (hasMap) {
                            setShowLoader(true);
                            setLoaderOperation('regenerating');
                            setLoaderProgress(0);
                            setLoaderStage('Preparing to regenerate...');
                          }
                          onGenerateLevel(level.value);
                        }
                      }}
                      disabled={generating || !canGenerateThisLevel}
                      title={!canGenerateThisLevel ? getDisabledReason(level.value) : ''}
                      className={`absolute bottom-1 right-1 w-min text-xs px-3 py-1.5 rounded transition-colors flex items-center justify-center space-x-1 ${canGenerateThisLevel
                        ? hasMap
                          ? 'bg-amber-500 text-white hover:bg-amber-600'
                          : 'bg-electric-blue text-white hover:bg-darker-blue'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                      {generating && selectedLevel === level.value ? (
                        <>
                          <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Generating...</span>
                        </>
                      ) : hasMap ? (
                        <>
                          <span>🔄</span>
                          <span>Regenerate</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-3 h-3" />
                          <span>Generate</span>
                        </>
                      )}
                    </button>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {!hasAllPrerequisites && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-800">
                Complete document analysis and process synthesis to enable level generation.
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProcessMapVisualization = () => {
    if (!hasDocuments) {
      return (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="max-w-md mx-auto">
            <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No Documents Uploaded</h3>
            <p className="text-gray-500 mb-6">
              Process maps are generated from your uploaded documents. Please upload documents first to continue.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-medium text-blue-800 mb-1">Getting Started:</p>
                  <ol className="text-sm text-blue-700 space-y-1">
                    <li>1. Upload documents containing process information</li>
                    <li>2. Run document analysis</li>
                    <li>3. Complete process synthesis</li>
                    <li>4. Generate process maps</li>
                  </ol>
                </div>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/app/documents'}
              className="bg-electric-blue text-white px-6 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2 mx-auto"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Documents</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    if (!hasAnalysis) {
      return (
        <div className="text-center py-12 bg-amber-50 rounded-lg border-2 border-dashed border-amber-300">
          <div className="max-w-md mx-auto">
            <FileText className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Documents Need Analysis</h3>
            <p className="text-gray-600 mb-4">
              You have {documentCount} document{documentCount !== 1 ? 's' : ''} uploaded, but they haven't been analyzed yet.
            </p>
            <div className="bg-amber-100 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-medium text-amber-800 mb-1">Next Steps:</p>
                  <ol className="text-sm text-amber-700 space-y-1">
                    <li>1. ✅ Documents uploaded ({documentCount} files)</li>
                    <li>2. 🔄 Run document analysis</li>
                    <li>3. ⏳ Complete process synthesis</li>
                    <li>4. ⏳ Generate process maps</li>
                  </ol>
                </div>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/app/sop-builder'}
              className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors flex items-center space-x-2 mx-auto"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Start Analysis</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    if (!hasSynthesis) {
      // Check if we already have process maps (legacy check for existing data)
      const hasExistingMaps = allProcessMaps && allProcessMaps.length > 0;

      if (hasExistingMaps) {
        // If we have existing maps but no synthesis, show a different message
        return (
          <div className="text-center py-12 bg-orange-50 rounded-lg border-2 border-dashed border-orange-300">
            <div className="max-w-md mx-auto">
              <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">New Documents Detected</h3>
              <p className="text-gray-600 mb-4">
                You've uploaded new documents that haven't been analyzed yet. Please analyze all documents and update process synthesis to regenerate accurate process maps.
              </p>
              <div className="bg-orange-100 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-orange-800 mb-1">Required Actions:</p>
                    <ol className="text-sm text-orange-700 space-y-1">
                      <li>1. ✅ Documents uploaded ({documentCount} files)</li>
                      <li>2. 🔄 Analyze all documents</li>
                      <li>3. 🔄 Update process synthesis</li>
                      <li>4. 🔄 Regenerate process maps</li>
                    </ol>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => window.location.href = '/app/sop-builder'}
                  className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-2"
                >
                  <FileText className="w-4 h-4" />
                  <span>Analyze Documents</span>
                </button>
              </div>
            </div>
          </div>
        );
      }

      // Original synthesis required message for projects without existing maps
      return (
        <div className="text-center py-12 bg-purple-50 rounded-lg border-2 border-dashed border-purple-300">
          <div className="max-w-md mx-auto">
            <BarChart3 className="w-16 h-16 text-purple-500 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Process Synthesis Required</h3>
            <p className="text-gray-600 mb-4">
              Documents have been analyzed, but process synthesis hasn't been completed yet. Process maps depend on synthesis results.
            </p>
            <div className="bg-purple-100 border border-purple-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-medium text-purple-800 mb-1">Progress:</p>
                  <ol className="text-sm text-purple-700 space-y-1">
                    <li>1. ✅ Documents uploaded ({documentCount} files)</li>
                    <li>2. ✅ Document analysis completed</li>
                    <li>3. 🔄 Complete process synthesis</li>
                    <li>4. ⏳ Generate process maps</li>
                  </ol>
                </div>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/app/sop-builder'}
              className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-2 mx-auto"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Run Process Synthesis</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    if (loadingRoleAssignments) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-electric-blue mx-auto mb-4"></div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">Loading Role Assignments</h3>
          <p className="text-gray-500 mb-4 max-w-md mx-auto">
            Generating intelligent role assignments for process steps...
          </p>
        </div>
      );
    }

    // FIXED: Check for actual process map existence
    const hasValidProcessMap = currentMap && currentMap.map_data;

    if (!hasValidProcessMap) {
      return (
        <div className="text-center py-12">
          <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            No Process Map Generated for {processLevels.find(l => l.value === selectedLevel)?.name}
          </h3>
          <p className="text-gray-500 mb-4 max-w-md mx-auto">
            Generate BPMN-compliant process maps from your analyzed documents with template integration.
            This level will show {processLevels.find(l => l.value === selectedLevel)?.description.toLowerCase()}.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 max-w-md mx-auto">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mt-0.5">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-green-800 mb-1">Ready to Generate:</p>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>✅ Documents uploaded and analyzed</li>
                  <li>✅ Process synthesis completed</li>
                  <li>✅ All prerequisites met</li>
                </ul>
              </div>
            </div>
          </div>
          <Tooltip text="Generate a comprehensive process map from your analyzed documents. This will create visual process flows, swimlanes, and detailed step analysis based on the selected level.">
            <button
              onClick={generateMap}
              disabled={generating}
              className="bg-electric-blue text-white px-6 py-2 rounded-lg hover:bg-darker-blue transition-colors disabled:opacity-50 flex items-center space-x-2 mx-auto"
            >
              <Play className="w-4 h-4" />
              <span>{generating ? `Generating Level ${selectedLevel}...` : `Generate Level ${selectedLevel} Map`}</span>
            </button>
          </Tooltip>
        </div>
      );
    }

    return (
      <div className="space-y-6" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
          {user?.role === 'SUPER_ADMIN' && (
            <div className="inline-flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setDiagramMode('interactive')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${diagramMode === 'interactive'
                    ? 'bg-white text-electric-blue shadow-sm border border-gray-200'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Eye className="w-4 h-4 inline mr-2" />
                Interactive View
              </button>
              <button
                onClick={() => setDiagramMode('professional')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${diagramMode === 'professional'
                    ? 'bg-white text-purple-600 shadow-sm border border-gray-200'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Zap className="w-4 h-4 inline mr-2" />
                Professional Diagram
              </button>
            </div>
          )}

            {selectedLevel === 3 && (
              <button
                onClick={handleToggleEditMode}
                disabled={generating || loading}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm flex items-center space-x-2 ${
                  isEditMode
                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                    : 'bg-green-600 text-white hover:bg-green-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isEditMode ? (
                  <>
                    <X className="w-4 h-4" />
                    <span>Cancel Edit</span>
                  </>
                ) : (
                  <>
                    <Edit3 className="w-4 h-4" />
                    <span>Edit Mode</span>
                  </>
                )}
              </button>
            )}

            {/* Gap Statistics Toggle Button */}
            {currentMap && (
              <button
                onClick={() => setShowGapStatistics(!showGapStatistics)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm flex items-center space-x-2 ${
                  showGapStatistics
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>{showGapStatistics ? 'Hide' : 'Show'} Gap Status</span>
              </button>
            )}

            {/* Friction Heatmap Toggle Button */}
            {currentMap && (
              <Tooltip text="Toggle friction heatmap to visualize where value leaks occur. Steps are colored from green (low friction) to red (critical friction) based on error rates, manual effort, cycle time, handoffs, and exceptions.">
                <button
                  onClick={handleToggleFrictionHeatmap}
                  disabled={frictionLoading}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm flex items-center space-x-2 ${
                    showFrictionHeatmap
                      ? 'bg-gradient-to-r from-purple-600 to-orange-500 text-white hover:from-purple-700 hover:to-orange-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${frictionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {frictionLoading ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Zap className="w-4 h-4" />
                  )}
                  <span>{showFrictionHeatmap ? 'Hide' : 'Show'} Friction</span>
                </button>
              </Tooltip>
            )}

            {/* Heatmap Chart Button */}
            {currentMap && (
              <button
                onClick={() => setShowHeatmapChart(v => !v)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm flex items-center space-x-2 ${
                  showHeatmapChart
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>{showHeatmapChart ? 'Hide' : 'Heatmap'} Chart</span>
              </button>
            )}

            {/* Estimate Friction Button - only show when heatmap is on and no/incomplete data */}
            {currentMap && showFrictionHeatmap && (
              <Tooltip text="Use AI to estimate friction metrics for steps without grounded data">
                <button
                  onClick={handleEstimateFriction}
                  disabled={frictionEstimating || frictionLoading}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 transition-all shadow-sm flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {frictionEstimating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-purple-700 border-t-transparent rounded-full animate-spin"></div>
                      <span>Estimating...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      <span>Estimate</span>
                    </>
                  )}
                </button>
              </Tooltip>
            )}

            {isEditMode && hasUnsavedChanges && (
              <button
                onClick={handleSaveChanges}
                disabled={savingChanges}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-electric-blue text-white hover:bg-darker-blue transition-all shadow-sm flex items-center space-x-2 disabled:opacity-50"
              >
                {savingChanges ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            )}
          </div>

          <div className="flex items-center space-x-3">
          <Tooltip text={
            currentMap?.level !== 3
              ? "Step Validation is only available for Level 3 (Operational) process maps"
              : validationId
                ? "Open Step Validation to quickly triage and approve validation results"
                : validating
                  ? "Validation in progress..."
                  : "Start validation to access Step Validation"
          }>
            <button
              onClick={() => {
                if (currentMap?.level !== 3) {
                  toast.error('Step Validation is only available for Level 3 (Operational) process maps');
                  return;
                }
                if (validationId) {
                  setShowCommandCenter(true);
                } else if (!validating) {
                  startValidationFromButton();
                }
              }}
              disabled={validating || currentMap?.level !== 3}
              className={`ml-4 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 shadow-sm ${
                currentMap?.level !== 3
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : validationId
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : validating
                      ? 'bg-blue-500 text-white'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
              } disabled:opacity-70 disabled:cursor-not-allowed`}
            >
              {validating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Validating...</span>
                </>
              ) : validationId ? (
                <>
                  <Zap className="w-4 h-4" />
                  <span>Step Validation</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span>Start Validation</span>
                </>
              )}
            </button>
          </Tooltip>
          </div>
        </div>

        {/* Validation Progress Bar - shown when validating */}
        {validating && validationProgress && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800">
                Validation in progress...
              </span>
              <span className="text-sm text-blue-600 font-semibold">
                {validationProgress.completed_steps !== undefined
                  ? `${validationProgress.completed_steps}/${validationProgress.total_steps} steps`
                  : 'Starting...'}
              </span>
            </div>
            {validationProgress.estimated_progress !== undefined && (
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${validationProgress.estimated_progress}%` }}
                />
              </div>
            )}
            <p className="text-xs text-blue-700 mt-2">
              Processing {validationProgress.total_steps || 0} process steps. This may take several minutes.
            </p>
          </div>
        )}

        {/* Heatmap Chart — shown below toolbar, above visualization */}
        {showHeatmapChart && currentMap && (
          <div className="mt-4">
            <FrictionHeatmapChart
              processMapId={currentMap.id}
              processMapName={currentMap.process_name}
            />
          </div>
        )}

        {/* Process Map Visualization with Gap Status Sidebar */}
        <div className="relative" style={{ width: '100%', maxWidth: '100%', minHeight: '600px' }}>
          {/* Main Visualization - Full width, sidebar overlays on top */}
          <div style={{ overflow: 'hidden' }}>
            {diagramMode === 'interactive' ? (
              <div className="relative">
                <SwimlanesVisualization
                  key={`${currentMap?.id}-${selectedLevel}-${currentMap?.updated_at}-${mapRefreshCounter}`}
                  swimlanes={swimlanes}
                  processes={processes}
                  selectedLevel={selectedLevel}
                  analysisResults={analysisResults}
                  showAuditInfo={true}
                  currentMap={currentMap}
                  auditEnhancementStatus={auditEnhancementStatus}
                  zoomLevel={zoomLevel}
                  onZoomChange={handleZoomChange}
                  isEditMode={isEditMode}
                  onProcessReorder={handleProcessReorder}
                  onStepDelete={handleStepDelete}
                  showFrictionHeatmap={showFrictionHeatmap}
                  frictionDataMap={frictionMap}
                />
                <FrictionLegend isVisible={!!(showFrictionHeatmap && frictionData?.steps?.some(s => s.has_data))} />
              </div>
            ) : (
              <DrawIOClientEditor
                mapId={currentMap?.id || 0}
                mapTitle={currentMap?.title || `Level ${selectedLevel} Process Map`}
              />
            )}
          </div>

          {/* Gap Status Sidebar - Slides in from right within visualization area */}
          {currentMap && (
            <div
              className={`absolute top-0 right-0 h-full bg-white shadow-2xl border-l border-gray-200 overflow-y-auto transition-transform duration-300 ease-in-out ${
                showGapStatistics ? 'translate-x-0' : 'translate-x-full'
              }`}
              style={{
                width: '400px',
                maxWidth: '90vw',
                zIndex: 50
              }}
            >
              <ProcessMapStatistics
                processes={processes}
                elements={currentMap?.map_data?.elements}
                currentMap={currentMap}
                selectedLevel={selectedLevel}
                onClose={() => setShowGapStatistics(false)}
              />
            </div>
          )}

          {/* Backdrop overlay when sidebar is open - only covers visualization area */}
          {showGapStatistics && (
            <div
              className="absolute inset-0 bg-black bg-opacity-30 transition-opacity duration-300"
              style={{ zIndex: 40 }}
              onClick={() => setShowGapStatistics(false)}
            />
          )}
        </div>
      </div>
    );
  };



  useEffect(() => {
    const mapForLevel = allProcessMaps.find(map => map.level === selectedLevel);
    setCurrentMap(mapForLevel || null);

    // ✅ FIX: Don't clear validationId here - let fetchValidationStatus manage it
    // This prevents the button from incorrectly showing "Start Validation" after revalidation
    // Only close the command center if level changes (not on map reloads)
    if (showCommandCenter && currentMap?.level !== selectedLevel) {
      setShowCommandCenter(false);
    }
  }, [allProcessMaps, selectedLevel, currentProject?.id]);


  const hasAllPrerequisites = hasDocuments && hasAnalysis && hasSynthesis;

  useEffect(() => {
    if (currentProject) {
      loadProcessMaps();
    }
  }, [currentProject, loadProcessMaps]);

  useEffect(() => {
    if (currentProject && selectedLevel === 1 && hasAllPrerequisites) {
      const { processes } = getProcessData();
      if (processes.length > 0) {
        setEnhancedProcesses(processes);
      }
    }
  }, [currentProject, selectedLevel, hasAllPrerequisites]);

  useEffect(() => {
    if (showFrictionHeatmap && currentMap?.id) {
      fetchFrictionData(currentMap.id);
    } else if (!showFrictionHeatmap) {
      clearFrictionData();
    }
  }, [showFrictionHeatmap, currentMap?.id, fetchFrictionData, clearFrictionData]);

  const handleToggleFrictionHeatmap = useCallback(() => {
    setShowFrictionHeatmap(prev => !prev);
  }, []);

  const handleEstimateFriction = useCallback(async () => {
    if (currentMap?.id) {
      await estimateFriction(currentMap.id);
    }
  }, [currentMap?.id, estimateFriction]);

  return (
    <>
      <div className="space-y-6 w-full max-w-full overflow-x-hidden">
        <div ref={canvasRef}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-super-dark-purple">Process Maps & Flow Analysis</h1>
              <p className="text-gray-600">Comprehensive process visualization and detailed flow documentation</p>
            </div>
            <div>
              {currentProject && (
                <ClearCacheButton projectId={currentProject!.id ?? 'xyz'} />
              )}
            </div>
          </div>

          {hasAllPrerequisites && (
            <ProcessLevelControls
              selectedLevel={selectedLevel}
              onLevelChange={handleLevelChange}
              onGenerateLevel={generateMapForLevel}
              generating={generating}
              hasAllPrerequisites={hasAllPrerequisites}
              allProcessMaps={allProcessMaps}
            />
          )}

          {!currentProject && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                <span className="text-yellow-800">Please select a project to generate process maps.</span>
              </div>
            </div>
          )}

        </div>
      </div>

      {showLoader && (
        <ProcessMapLoader
          selectedLevel={selectedLevel}
          operation={loaderOperation}
          processLevels={processLevels}
          progress={loaderProgress}
          stage={loaderStage}
        />
      )}

      <div className="p-6 min-h-[400px] bg-gray-50">
        {loading && !showLoader ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-electric-blue"></div>
          </div>
        ) : (
          renderProcessMapVisualization()
        )}

        {/* Validation Report Panel - positioned below the main process map diagram */}
        {currentProject && currentMap && hasAllPrerequisites && (
          <div className="mt-6">
            <ValidationReportPanel
              key={`validation-report-${currentMap.id}-${mapRefreshCounter}`}
              projectId={currentProject.id}
              processMapId={currentMap.id}
            />
          </div>
        )}

        {/* Validation Command Center */}
        {currentProject && currentMap && validationId && (
          <ValidationCommandCenter
            key={`${currentProject.id}-${currentMap.id}-${validationId}`}
            isOpen={showCommandCenter}
            onClose={() => setShowCommandCenter(false)}
            processMapId={currentMap.id}
            projectId={currentProject.id}
            validationId={validationId}
            onValidationUpdate={async () => {
              // ✅ FIX: Use React Query to invalidate cache and refetch data
              await queryClient.invalidateQueries({
                queryKey: ['processMaps', currentProject.id]
              });

              // Force reload process maps with fresh data
              await loadProcessMaps();

              // Fetch updated validation status
              await fetchValidationStatus();

              // ✅ Force re-render of process map visualization
              setMapRefreshCounter(prev => prev + 1);

              toast.success('Process map updated successfully');
            }}
          />
        )}
      </div>
    </>
  );
};

export default ProcessMaps;